<template>
  <div class="cosmic-velocity-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div ref="canvasContainer" class="canvas-3d-wrapper">
      <!-- HUD Overlay -->
      <div class="hud-top">
        <div class="hud-bit">
          <label>当前速度</label>
          <span :class="speedColor">{{ velocity.toFixed(2) }} km/s</span>
        </div>
        <div class="hud-bit">
          <label>轨道形态</label>
          <span>{{ orbitType }}</span>
        </div>
      </div>

      <!-- 推导过程悬浮层 -->
      <div class="derivation-floating glass" v-if="isDerivationMode">
        <h4>第三宇宙速度推导核心步骤</h4>
        <div class="step">1. 相对地球：需达到逃逸速度 $v_1 = 11.2$ km/s</div>
        <div class="step">2. 相对太阳：需加速到 $v_2 = 42.1$ km/s</div>
        <div class="step">3. 利用助推：地球公转速度 $v_3 = 29.8$ km/s</div>
        <div class="formula-result">$v_{发} = \sqrt{v_1^2 + (v_2-v_3)^2} \approx 16.7$ km/s</div>
      </div>
    </div>

    <div class="control-panel">
      <div class="launch-card">
        <div class="slider-box">
          <label>发射初速度 (v₀)</label>
          <el-slider v-model="velocity" :min="1" :max="22" :step="0.1" size="mini"></el-slider>
        </div>
        <div class="btn-group">
          <el-button type="primary" size="small" @click="launch" icon="el-icon-position">模拟发射</el-button>
          <el-button v-if="isDerivationMode" type="warning" size="small" @click="velocity = 16.7">设为 16.7</el-button>
        </div>
      </div>
      <div class="info-card">
        <p v-if="velocity < 7.9"><strong>坠落：</strong>由于线速度不足，向心力由重力完全提供并导致半径缩小，最终坠入大气层。</p>
        <p v-else-if="velocity < 11.2"><strong>第一宇宙速度：</strong>卫星开始以闭合轨道（圆/椭圆）环绕地球运行。</p>
        <p v-else-if="velocity < 16.7"><strong>第二宇宙速度：</strong>动能足以克服地球引力做功，轨迹由闭合曲线变为抛物线/双曲线。</p>
        <p v-else><strong>第三宇宙速度：</strong>卫星摆脱太阳系引力束缚，进入星际航行阶段。</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'CosmicVelocityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      velocity: 7.9,
      running: true,
      isDerivationMode: false,
      orbitTime: 0
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '宇宙速度与深空探索仿真';
    },
    activeFormula() {
       if (this.velocity < 11.2) return 'v = √(GM/r)';
       return 'v_e = √(2GM/r)';
    },
    orbitType() {
      if (this.velocity < 7.9) return '坠落轨迹';
      if (Math.abs(this.velocity - 7.9) < 0.1) return '理想圆轨道';
      if (this.velocity < 11.2) return '椭圆轨道';
      if (this.velocity < 16.7) return '逃逸(地球)';
      return '逃逸(太阳系)';
    },
    speedColor() {
       if (this.velocity < 7.9) return 'low';
       if (this.velocity < 11.2) return 'mid';
       return 'high';
    }
  },
  mounted() {
    this.detectMode();
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  watch: {
    velocity() { this.updateOrbit(); },
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (kp.includes('第一')) this.velocity = 7.9;
      else if (kp.includes('第二')) this.velocity = 11.2;
      else if (kp.includes('第三') || kp.includes('发射速度')) this.velocity = 16.7;
      
      if (kp.includes('推导')) {
        this.isDerivationMode = true;
      }
    },
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      if (!this.canvasContainer) return;
      
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 2000);
      this.camera.position.set(150, 100, 150);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Earth
      const earthGeo = new THREE.SphereGeometry(20, 32, 32);
      const earthMat = new THREE.MeshPhongMaterial({
        color: 0x2233ff, emissive: 0x112244, shininess: 30
      });
      this.earth = new THREE.Mesh(earthGeo, earthMat);
      this.scene.add(this.earth);

      // Lights
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.3));
      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(100, 100, 100);
      this.scene.add(light);

      // Satellite
      const satGeo = new THREE.SphereGeometry(2, 16, 16);
      const satMat = new THREE.MeshBasicMaterial({ color: 0x00d2ff });
      this.satellite = new THREE.Mesh(satGeo, satMat);
      this.scene.add(this.satellite);

      // Orbit Line
      this.orbitGroup = new THREE.Group();
      this.scene.add(this.orbitGroup);
      this.updateOrbit();

      this.animate();
    },
    updateOrbit() {
      if (this.orbitLine) this.orbitGroup.remove(this.orbitLine);
      
      const points = [];
      const v = this.velocity;
      const R = 20;
      
      const e = (v/7.9) - 1; 
      
      if (v < 7.9) {
          for(let t=0; t<Math.PI*0.8; t+=0.1) {
              const r = R + (1 - t/Math.PI)*20;
              points.push(new THREE.Vector3(Math.cos(t)*r, 0, Math.sin(t)*r));
          }
      } else {
          for(let t=0; t<Math.PI*2; t+=0.1) {
              const r = (1 + 0) / (1 + e * Math.cos(t)) * (R+20);
              if (r > 0 && r < 500) {
                 points.push(new THREE.Vector3(Math.cos(t)*r, 0, Math.sin(t)*r));
              }
          }
      }
      
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineBasicMaterial({ color: 0x00d2ff, transparent: true, opacity: 0.4 });
      this.orbitLine = new THREE.Line(geo, mat);
      this.orbitGroup.add(this.orbitLine);
    },
    launch() {
      this.orbitTime = 0;
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      this.orbitTime += 0.02;
      const v = this.velocity;
      const R = 40;
      const e = v/7.9 - 1;
      
      if (v >= 7.9) {
          const t = this.orbitTime;
          const r = (1 + 0) / (1 + e * Math.cos(t)) * R;
          this.satellite.position.x = Math.cos(t) * r;
          this.satellite.position.z = Math.sin(t) * r;
      } else {
          this.satellite.position.set(R+5, 0, 0);
      }

      this.earth.rotation.y += 0.005;
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.cosmic-velocity-pro { flex: 1; display: flex; flex-direction: column; gap: 15px; height: 100%; overflow: hidden; }
.viz-header { 
  display: flex; justify-content: space-between; align-items: center; 
  .brief { font-size: 1rem; color: #fff; font-weight: 700; }
  .formula-chip { color: #facc15; background: rgba(250, 204, 21, 0.1); border: 1px solid #facc15; padding: 4px 12px; border-radius: 6px; font-weight: bold; font-family: 'Times New Roman', serif; }
}
.canvas-3d-wrapper {
  flex: 1.2; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top {
    position: absolute; top: 15px; left: 15px; display: flex; gap: 12px; z-index: 5;
    .hud-bit {
      background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(10px); border-left: 3px solid #00d2ff; padding: 6px 12px; border-radius: 6px;
      label { display: block; font-size: 10px; color: #94a3b8; text-transform: uppercase; margin-bottom: 2px; }
      span { font-size: 14px; color: #fff; font-family: monospace; font-weight: bold; }
      .low { color: #ff4757; } .mid { color: #2ed573; } .high { color: #ffa502; }
    }
  }
}
.derivation-floating {
  position: absolute; top: 15px; right: 15px; width: 240px; background: rgba(30, 41, 59, 0.85); border: 1px solid rgba(0, 210, 255, 0.3); padding: 15px; border-radius: 10px; backdrop-filter: blur(10px); z-index: 10;
  h4 { color: #00d2ff; margin: 0 0 10px 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; }
  .step { color: #e2e8f0; font-size: 12px; margin-bottom: 6px; line-height: 1.4; }
  .formula-result { background: rgba(0, 210, 255, 0.1); padding: 8px; border-radius: 6px; color: #fff; font-weight: bold; font-family: serif; text-align: center; margin-top: 10px; border: 1px dashed rgba(0, 210, 255, 0.4); }
}
.control-panel {
  flex: 0.8; display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 15px; overflow-y: auto; padding-bottom: 10px;
  .launch-card { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 15px;
    .slider-box { label { display: block; color: #94a3b8; font-size: 12px; font-weight: 700; margin-bottom: 8px; } }
    .btn-group { margin-top: 15px; display: flex; gap: 8px; }
  }
  .info-card { background: rgba(14, 165, 233, 0.05); border: 1px solid rgba(14, 165, 233, 0.1); border-radius: 12px; padding: 15px;
    p { margin: 0; font-size: 12px; color: #cbd5e1; line-height: 1.6; strong { color: #fff; } }
  }
}
</style>
